import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar, Clock, Users, Video, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface MeetingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: {
    title: string;
    scheduled_at: string;
    meeting_url?: string;
    attendees?: string[];
  } | null;
}

export function MeetingConfirmationModal({ isOpen, onClose, meeting }: MeetingConfirmationModalProps) {
  if (!meeting) return null;

  const scheduledDate = new Date(meeting.scheduled_at);
  const formattedDate = format(scheduledDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const formattedTime = format(scheduledDate, "HH:mm", { locale: ptBR });

  const handleCopyLink = () => {
    if (meeting.meeting_url) {
      navigator.clipboard.writeText(meeting.meeting_url);
      toast.success('Link copiado para área de transferência!');
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-green-100">
            <Calendar className="w-6 h-6 text-green-600" />
          </div>
          <AlertDialogTitle className="text-center text-xl">
            ✅ Reunião Agendada com Sucesso!
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-4">
              <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                <div>
                  <h4 className="font-semibold text-foreground mb-1">{meeting.title}</h4>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{formattedDate}</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{formattedTime}</span>
                </div>
                
                {meeting.attendees && meeting.attendees.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <span className="text-foreground">
                        {meeting.attendees.join(', ')}
                      </span>
                    </div>
                  </div>
                )}
                
                {meeting.meeting_url && (
                  <div className="flex items-center gap-2 text-sm pt-2 border-t">
                    <Video className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground truncate flex-1">{meeting.meeting_url}</span>
                  </div>
                )}
              </div>
              
              {meeting.meeting_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="w-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Link da Reunião
                </Button>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} className="w-full">
            OK, Entendi
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
